/**
 * CustomItemSearch
 * -----------------------------------------------------------------------------
 * Inline type-to-filter combobox that lives in the Service Notes Quote
 * Builder header. Same job as the Service-Package picker on the Invoice
 * page, but optimised for the SM's *"customer wants a wiper replace"*
 * micro-flow:
 *
 *   1. SM types `wiper` → matches every saved Custom Item Template
 *      whose name OR any line item description contains "wiper".
 *   2. SM clicks a result → ALL line items in that template are pushed
 *      onto `notes_items` as fully pre-filled quote rows
 *      (description, qty, ORP, RRP, margin, labour hrs, labour rate,
 *       supplier, part number, category). Everything stays editable.
 *   3. Closes on outside-click / Esc / row pick.
 *
 * The component is data-only — it never mutates state directly. Callers
 * supply `onPickRows(rows)` which receives an array of brand-new
 * notes_items shapes ready to splice into the invoice.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search, Wand2, X, Wrench, Package, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { computeTemplateLineTotal, DEFAULT_MARGIN_PCT } from '@/lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Convert one template line_item into the notes_items shape the modal
// expects. NULL is used wherever the template intentionally left a field
// blank — the SM tweaks per-case before customer approval.
const templateLineToNotesItem = (line, workshopRate, idx = 0) => {
  const cat = line.category || 'part_and_labour';
  const isTyre = cat === 'tyre';
  const wantsParts = cat === 'part' || cat === 'part_and_labour' || isTyre;
  const wantsLabour = cat === 'labour' || cat === 'part_and_labour';

  // Structured shape — SM entered parts_price + margin separately. Use as-is.
  const structuredPartsPrice = wantsParts && line.parts_price != null ? Number(line.parts_price) : 0;
  // Detect "legacy" templates that only stored `unit_price` (the rolled-up
  // headline figure with margin baked in). For those we fall back to
  // unit_price as the parts_price AND zero out the margin so we don't
  // re-apply markup on top of an already-marked-up number.
  const isLegacyOnlyUnitPrice =
    wantsParts && structuredPartsPrice === 0 && Number(line.unit_price || 0) > 0;
  const partsPriceFinal = isLegacyOnlyUnitPrice
    ? Number(line.unit_price)
    : structuredPartsPrice;
  const margin = isTyre
    ? 30  // tyres locked at 30%
    : (wantsParts
        ? (
            isLegacyOnlyUnitPrice
              ? 0  // unit_price already includes markup — don't double-apply.
              : (line.parts_margin_pct != null ? Number(line.parts_margin_pct) : DEFAULT_MARGIN_PCT)
          )
        : null);
  const labourHours = (wantsLabour && !isTyre) && line.labour_hours != null ? Number(line.labour_hours) : (isTyre ? null : 0);
  const labourRate = (wantsLabour && !isTyre)
    ? (line.labour_rate != null && Number(line.labour_rate) > 0
        ? Number(line.labour_rate)
        : (workshopRate || 0))
    : (isTyre ? null : 0);
  const row = {
    line_id: `note_${Math.random().toString(36).slice(2, 10)}_${idx}`,
    category: cat,
    description: line.description || '',
    quantity: Number(line.quantity) || (isTyre ? 4 : 1),
    parts_price: wantsParts ? partsPriceFinal : 0,
    parts_cost_price: wantsParts && line.parts_cost_price != null ? Number(line.parts_cost_price) : null,
    parts_margin_pct: margin,
    parts_supplier: wantsParts ? (line.parts_supplier || null) : null,
    part_number: wantsParts ? (line.part_number || null) : null,
    // Tyre-specific add-on. Pulled through from the template so saved
    // tyre templates can carry their flat-rate (e.g. "$25 fit + balance").
    tyre_flat_rate: isTyre
      ? (line.tyre_flat_rate != null ? Number(line.tyre_flat_rate) : 0)
      : null,
    labour_hours: labourHours,
    labour_rate: labourRate,
    approved: false,
    order: 0,
    imported_from_template_id: null,
    imported_from_template_name: null,
  };
  row.line_total = computeTemplateLineTotal(row, workshopRate);
  return row;
};

export default function CustomItemSearch({ onPickRows, workshopRate = 0, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch on every open — templates are small (<100 docs) and the SM
  // might have just created a fresh one in another tab. Re-fetching on
  // each open avoids the stale-cache footgun without paying a per-
  // keystroke roundtrip cost.
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/templates?category=invoice_template`,
          { credentials: 'include' },
        );
        if (!cancel && res.ok) {
          const data = await res.json();
          setTemplates(data.templates || []);
          setLoaded(true);
        }
      } catch (e) {
        // Log but don't toast — an empty dropdown with the empty-state
        // copy is more useful than a popup the SM can't fix.
        if (!cancel) {
          console.warn('CustomItemSearch: template fetch failed', e);
        }
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  // Close on click-outside / Esc.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Auto-focus the input every time the picker opens.
  useEffect(() => {
    if (open) {
      // RAF so the input is in the DOM before .focus() runs.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Filter templates by name OR any line-item description containing the
  // typed query. Empty query → return all so the SM can browse.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => {
      if ((t.name || '').toLowerCase().includes(q)) return true;
      if ((t.content || '').toLowerCase().includes(q)) return true;
      const lines = t.line_items || [];
      return lines.some(li => (li.description || '').toLowerCase().includes(q));
    });
  }, [query, templates]);

  const pickTemplate = (tpl) => {
    const lines = tpl.line_items || [];
    if (lines.length === 0) {
      toast.error('This template has no line items');
      return;
    }
    const newRows = lines.map((li, idx) => {
      const row = templateLineToNotesItem(li, workshopRate, idx);
      row.imported_from_template_id = tpl.template_id;
      row.imported_from_template_name = tpl.name;
      return row;
    });
    onPickRows(newRows);
    toast.success(`Added ${newRows.length} row${newRows.length === 1 ? '' : 's'} from "${tpl.name}"`);
    setQuery('');
    setOpen(false);
  };

  // Compact preview $ for a template — sum of all line totals using the
  // SHARED template helper so this matches the Manage Templates UI 1:1.
  // (Previously used `computeLineTotal` directly which returned $0 for
  // legacy rows that only stored `unit_price`. Manage UI had a fallback;
  // picker didn't — same data, two displays. Now unified.)
  const templateTotal = (tpl) => {
    const lines = tpl.line_items || [];
    if (!lines.length) return 0;
    return lines.reduce((sum, li) => sum + computeTemplateLineTotal(li, workshopRate), 0);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="snm-custom-item-search-wrap">
      {!open ? (
        // ─── TRIGGER PILL ─────────────────────────────────────────────
        // Vibrant pill so it doesn't look "dull" amongst the other inline
        // actions. Indigo→violet gradient with a sparkle icon — visually
        // separates from neutral controls and signals "this magic-picks
        // a saved template".
        <button
          type="button"
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
          className="
            inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold
            text-white shadow-sm
            bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500
            hover:from-indigo-600 hover:via-violet-600 hover:to-fuchsia-600
            hover:shadow-md hover:scale-[1.03] active:scale-[0.98]
            transition-all
            disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
          "
          data-testid="snm-custom-item-search-open"
          title="Search saved Custom Items (e.g. wiper replace) and pre-fill the quote row"
        >
          <Wand2 className="h-3.5 w-3.5" />
          Add Custom Item
        </button>
      ) : (
        // ─── ACTIVE SEARCH PILL ────────────────────────────────────────
        <div
          className="flex items-center gap-1.5 bg-white border-2 border-indigo-400 ring-2 ring-indigo-100 rounded-full pl-3 pr-1 h-8 min-w-[16rem] shadow-sm"
          data-testid="snm-custom-item-search-active"
        >
          <Search className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search e.g. wiper, brake, oil…"
            className="h-6 border-0 p-0 text-xs focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
            data-testid="snm-custom-item-search-input"
          />
          <button
            type="button"
            onClick={() => { setOpen(false); setQuery(''); }}
            className="text-slate-400 hover:text-rose-500 p-1 rounded-full hover:bg-rose-50"
            data-testid="snm-custom-item-search-close"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[32rem] max-h-96 overflow-hidden bg-white border border-indigo-200 rounded-xl shadow-2xl z-50 ring-1 ring-indigo-100"
          data-testid="snm-custom-item-search-dropdown"
        >
          {/* Sticky header — gives the dropdown a clear identity & a
              live count of matches so the SM doesn't wonder if more
              items exist below the fold. */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-2 bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 border-b border-indigo-100">
            <div className="flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5 text-indigo-600" />
              <p className="text-[11px] font-bold tracking-wide uppercase text-indigo-900">
                Custom Item Templates
              </p>
            </div>
            <span
              className="text-[10px] font-semibold text-indigo-700 bg-white/80 px-2 py-0.5 rounded-full border border-indigo-200"
              data-testid="snm-custom-item-search-count"
            >
              {filtered.length} match{filtered.length === 1 ? '' : 'es'}
            </span>
          </div>

          <div className="overflow-y-auto max-h-[20rem]">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center" data-testid="snm-custom-item-search-empty">
                <Wand2 className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500 font-medium">
                  {!loaded
                    ? 'Loading templates…'
                    : query.trim()
                      ? `No templates match "${query}"`
                      : 'No saved Custom Item templates yet.'}
                </p>
                {loaded && !query.trim() && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Create one in <strong>Invoice Templates → Custom Items</strong>.
                  </p>
                )}
              </div>
            ) : (
              <div className="py-1.5">
                {filtered.map(tpl => {
                  const total = templateTotal(tpl);
                  const lineCount = (tpl.line_items || []).length;
                  // Count parts vs labour for the colored chip strip.
                  const parts = (tpl.line_items || []).filter(
                    li => (li.category || 'part_and_labour') !== 'labour'
                  ).length;
                  const labours = (tpl.line_items || []).filter(
                    li => (li.category || 'part_and_labour') !== 'part'
                            && (li.hours || li.labour_hours || li.quoted_labour_hours)
                  ).length;
                  return (
                    <button
                      key={tpl.template_id}
                      type="button"
                      onClick={() => pickTemplate(tpl)}
                      className="
                        w-full text-left px-3 py-2.5 mx-1 my-0.5 rounded-lg
                        hover:bg-gradient-to-r hover:from-indigo-50 hover:via-violet-50/60 hover:to-transparent
                        hover:shadow-sm
                        border border-transparent hover:border-indigo-200
                        transition-all group
                      "
                      data-testid={`snm-custom-item-pick-${tpl.template_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Template name — bigger, darker for a clear
                              primary line; brand-tinted on hover to
                              reinforce interactivity. */}
                          <p className="text-sm font-bold text-slate-900 group-hover:text-indigo-700 truncate">
                            {tpl.name}
                          </p>
                          {/* Description — was washed-out grey; now a
                              richer slate-600 with proper leading. */}
                          {tpl.content && (
                            <p className="text-xs text-slate-600 leading-snug mt-0.5 line-clamp-2">
                              {tpl.content}
                            </p>
                          )}
                          {/* Mini summary chips for what the template
                              contains — colour-coded so the SM scans
                              at a glance. */}
                          <div className="mt-1.5 flex flex-wrap items-center gap-1">
                            {parts > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-100 text-blue-800 rounded-full px-2 py-0.5">
                                <Package className="h-2.5 w-2.5" />
                                {parts} part{parts === 1 ? '' : 's'}
                              </span>
                            )}
                            {labours > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
                                <Wrench className="h-2.5 w-2.5" />
                                {labours} labour
                              </span>
                            )}
                            {(tpl.line_items || []).slice(0, 2).map((li, idx) => (
                              <span
                                key={idx}
                                className="text-[10px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 max-w-[10rem] truncate"
                                title={li.description}
                              >
                                {li.description}
                              </span>
                            ))}
                            {lineCount > 2 && (
                              <span className="text-[10px] text-slate-500 font-semibold">
                                +{lineCount - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Right-side stats block — line count + bright
                            green pill for the total so the SM sees the
                            "headline number" without scanning. */}
                        <div className="text-right shrink-0 flex flex-col items-end gap-1">
                          <span className="text-[10px] font-semibold text-slate-500">
                            {lineCount} line{lineCount === 1 ? '' : 's'}
                          </span>
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5 tabular-nums shadow-sm">
                            {fmt(total)}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
